// https://www.figma.com/file/iqgIDKDErTLUA2WS3NtOVv/Franklin-Design-System?node-id=2%3A4

import type { CSSObject } from 'styled-components';
import { SPACING_SCALING_FACTOR } from '../constants';
import { components } from '../theme-variations/components/components';
import type { ThemeComponents } from '../theme-variations/components/components.types';
import { palette } from '../theme-variations/palette';
import type { Palette } from '../theme-variations/palette/palette.type';

enum colorOptions {
  lightBlue = 'lightBlue',
  lightBlue7 = 'lightBlue7',
  lightBlue5 = 'lightBlue5',
  lightBlue3 = 'lightBlue3',
  lightBlue1 = 'lightBlue1',
  darkBlue = 'darkBlue',
  darkBlue7 = 'darkBlue7',
  darkBlue5 = 'darkBlue5',
  darkBlue3 = 'darkBlue3',
  darkBlue1 = 'darkBlue1',
  backgroundBlue = 'backgroundBlue',
  backgroundBlue7 = 'backgroundBlue7',
  backgroundBlue5 = 'backgroundBlue5',
  backgroundBlue3 = 'backgroundBlue3',
  backgroundBlue1 = 'backgroundBlue1',
  flamingoRed = 'flamingoRed',
  flamingoRed7 = 'flamingoRed7',
  flamingoRed5 = 'flamingoRed5',
  flamingoRed3 = 'flamingoRed3',
  flamingoRed1 = 'flamingoRed1',
  mellowOrange = 'mellowOrange',
  mellowOrange120 = 'mellowOrange120',
  mellowOrange7 = 'mellowOrange7',
  mellowOrange5 = 'mellowOrange5',
  mellowOrange3 = 'mellowOrange3',
  mellowOrange1 = 'mellowOrange1',
  errorRed = 'errorRed',
  errorRed7 = 'errorRed7',
  errorRed5 = 'errorRed5',
  errorRed3 = 'errorRed3',
  errorRed1 = 'errorRed1',
  successGreen = 'successGreen',
  successGreen7 = 'successGreen7',
  successGreen5 = 'successGreen5',
  successGreen3 = 'successGreen3',
  successGreen1 = 'successGreen1',
  black = 'black',
  grey = 'grey',
  darkGrey = 'darkGrey',
  lightGrey = 'lightGrey',
  middleGrey = 'middleGrey',
  whiteGrey = 'whiteGrey',
  white = 'white',
  midBlue = 'midBlue',
  moneyGreen = 'moneyGreen',
  moneyGreenBackground = 'moneyGreenBackground',
  red = 'red',
  orange = 'orange',
  backgroundGrey = 'backgroundGrey',
  background = 'background',
  darkOrange = 'darkOrange',
  lightPurple = 'lightPurple',
  backgroundLightGrey = 'backgroundLightGrey',
}

type colorKeys = keyof typeof colors;
export type colorCodes = (typeof colors)[colorKeys];

export type Colors = Record<colorOptions, colorCodes>;

export interface MediaQueries {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

export interface Breakpoints extends Array<string> {
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
}
export interface BreakpointsConfiguration {
  [key: string]: number;
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
}
interface Size {
  width: string;
  height: string;
}

interface BorderRadius {
  /** 4px - Buttons, banners, input fields, date picker, tables, tooltips, toasts */
  sm: string;
  /** 8px - Tiles, containers, cards */
  md: string;
  /** 12px - Modals */
  lg: string;
  /** 50% - Icon buttons */
  full: string;
  /** 999px - All corners rounded the same amount */
  rounded: string;
}

type Spacing = number | string;

export interface ITheme {
  /** @deprecated Use palette instead */
  colors: Colors;
  mediaQueries: MediaQueries;
  fontFamily: string;
  breakpoints: Breakpoints;
  breakpointConfiguration: BreakpointsConfiguration;
  input: {
    text: Size;
    textarea: Size;
    select: Size;
    date: Size;
  };
  boxShadow: string;
  boxShadowLight: string;
  borderRadius: BorderRadius;
  spacing: (
    spacingOne: Spacing,
    spacingTwo?: Spacing,
    spacingThree?: Spacing,
    spacingFour?: Spacing
  ) => string;
  typography: TTypographyVariantStyles;
  palette: Palette;
  components: ThemeComponents;
  isWhiteLabel: boolean;
}

const colors = {
  darkBlue: '#050F72',
  darkBlue7: '#50579C',
  darkBlue5: '#8287B8',
  darkBlue3: '#B4B7D5',
  darkBlue1: '#E6E7F1',
  backgroundBlue: '#303FA9',
  backgroundBlue7: '#6E79C3',
  backgroundBlue5: '#979FD4',
  backgroundBlue3: '#C1C5E5',
  backgroundBlue1: '#EAECF6',
  lightBlue: '#3B53D0',
  lightBlue7: '#7687DE',
  lightBlue5: '#9DA9E7',
  lightBlue3: '#C4CBF1',
  lightBlue1: '#EBEEFA',
  flamingoRed: '#FF6E75',
  flamingoRed7: '#FF9A9E',
  flamingoRed5: '#FFB6BA',
  flamingoRed3: '#FFD4D6',
  flamingoRed1: '#FFF1F1',
  mellowOrange: '#FFAF83',
  mellowOrange120: '#F08D56',
  mellowOrange7: '#FFC7A8',
  mellowOrange5: '#FFD7C1',
  mellowOrange3: '#FFE7DA',
  mellowOrange1: '#FFF7F3',
  errorRed: '#F85B46',
  errorRed7: '#FA8C7E',
  errorRed5: '#FBADA2',
  errorRed3: '#FDCEC8',
  errorRed1: '#FEEFED',
  successGreen: '#4CBB25',
  successGreen7: '#82CF66',
  successGreen5: '#A5DD92',
  successGreen3: '#C9EBBE',
  successGreen1: '#EDF8E9',
  black: '#000000',
  grey: '#8DA9C4',
  darkGrey: '#555555',
  middleGrey: '#C6D4E1',
  lightGrey: '#F2F4F5',
  whiteGrey: '#F7F8F9',
  white: '#FFFFFF',
  midBlue: '#303FA9',
  moneyGreen: '#4CBB25',
  moneyGreenBackground: '#EDF8E9',
  red: '#F85B46',
  orange: '#FF6E75',
  backgroundGrey: '#F7F8F9',
  background: '#E5E5E5',
  darkOrange: '#F85B46',
  lightPurple: '#EBEEFA',
  backgroundLightGrey: '#EBEEFA',
  lightOrange: '#FFAF83',
} as const;

const breakpoints: Breakpoints = ['576px', '1020px', '1280px', '1920px'];
breakpoints.sm = breakpoints[0];
breakpoints.md = breakpoints[1];
breakpoints.lg = breakpoints[2];
breakpoints.xl = breakpoints[3];

export enum TypographyVariant {
  h1 = 'h1',
  h2 = 'h2',
  h3 = 'h3',
  bodyLg1 = 'bodyLg1',
  bodyLg2 = 'bodyLg2',
  bodyLg3 = 'bodyLg3',
  bodyMd1 = 'bodyMd1',
  bodyMd2 = 'bodyMd2',
  bodyMd3 = 'bodyMd3',
  bodySm1 = 'bodySm1',
  bodySm2 = 'bodySm2',
  labelLg = 'labelLg',
  labelSm = 'labelSm',
  integerNumberM = 'integerNumberM',
  decimalNumberM = 'decimalNumberM',
  button = 'button',
  caption = 'caption',
}

const typographyVariantStyles = {
  h1: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: '2.125rem',
    letterSpacing: '0em',
    margin: 0,
  },
  h2: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: '2rem',
    letterSpacing: '0em',
    margin: 0,
  },
  h3: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: '1.75rem',
    letterSpacing: '0em',
    margin: 0,
  },
  bodyLg1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: '1.375rem',
    letterSpacing: '0.01em',
    margin: 0,
  },
  bodyLg2: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: '1.375rem',
    letterSpacing: '0.01em',
    margin: 0,
  },
  bodyLg3: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: '1.375rem',
    letterSpacing: '0.01em',
    margin: 0,
  },
  bodyMd1: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: '1.25rem',
    letterSpacing: '0.01em',
    margin: 0,
  },
  bodyMd2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: '1.25rem',
    letterSpacing: '0.01em',
    margin: 0,
  },
  bodyMd3: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: '1.25rem',
    letterSpacing: '0.01em',
    margin: 0,
  },
  labelLg: {
    fontSize: '0.688rem',
    fontWeight: 600,
    lineHeight: '1rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: colors.grey,
    margin: 0,
  },
  labelSm: {
    fontSize: '0.625rem',
    fontWeight: 600,
    lineHeight: '0.813rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: colors.grey,
    margin: 0,
  },
  bodySm1: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: '0.813rem',
    letterSpacing: '0.01em',
    margin: 0,
  },
  bodySm2: {
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: '0.813rem',
    letterSpacing: '0.01em',
    margin: 0,
  },
  integerNumberM: {
    color: colors.lightBlue,
    fontWeight: 700,
    fontSize: '1.5rem',
    lineHeight: '2rem',
    margin: 0,
  },
  decimalNumberM: {
    component: 'p',
    color: colors.lightBlue,
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    margin: 0,
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: '1.25rem',
    color: colors.lightBlue,
    letterSpacing: '0.01em',
    margin: 0,
  },
  caption: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: '1.25rem',
    color: colors.grey,
    letterSpacing: '0.01em',
    margin: 0,
  },
};

type TTypographyVariantStyles = Record<TypographyVariant, CSSObject>;

/**
 * @deprecated Use theme from ThemeProvider to respect whitelabelling.
 * @example
 * ```tsx
 * const theme = useTheme();
 * ```
 * @example
 * ```tsx
 * const DemoDiv = styled.div`
 *   ${({ theme }) => css`
 *     background-color: ${theme.palette.primary.main};
 *   `}
 * `;
 */
export const theme: ITheme = {
  colors: colors,
  mediaQueries: {
    xs: 'only screen and (max-width: 576px)',
    sm: 'only screen and (min-width: 577px) and (max-width: 768px)',
    md: 'only screen and (min-width: 769px) and (max-width: 1020px)',
    lg: 'only screen and (min-width: 1021px) and (max-width: 1280px)',
    xl: 'only screen and (min-width: 1281px)',
  },
  fontFamily: 'Saans, Arial',
  breakpoints: breakpoints,
  input: {
    text: {
      width: '335px',
      height: '38px',
    },
    textarea: {
      width: '335px',
      height: '180px',
    },
    select: {
      width: '335px',
      height: '38px',
    },
    date: {
      width: '335px',
      height: '38px',
    },
  },
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
  boxShadowLight: '0px 4px 10px rgba(154, 154, 154, 0.1)',
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    full: '50%',
    rounded: '999px',
  },
  breakpointConfiguration: {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 1020,
    xl: 1280,
  },
  spacing: getSpacing,
  typography: typographyVariantStyles,
  palette,
  components,
  isWhiteLabel: false,
} as const;

function getSpacing(
  spacingOne: Spacing,
  spacingTwo?: Spacing,
  spacingThree?: Spacing,
  spacingFour?: Spacing
) {
  if (
    (!isUndefined(spacingFour) &&
      (isUndefined(spacingThree) || isUndefined(spacingTwo))) ||
    (!isUndefined(spacingThree) && isUndefined(spacingTwo))
  ) {
    console.error(
      'Error: theme.spacing should not be passed undefined before a non-undefined value'
    );
    // return default value as to not break CSS
    return '0px';
  }
  const spacings = [spacingOne, spacingTwo, spacingThree, spacingFour];
  const filteredSpacings = spacings.filter(
    (result): result is Spacing =>
      typeof result === 'number' || typeof result === 'string'
  );
  return filteredSpacings
    .map((spacing) =>
      typeof spacing === 'string'
        ? spacing
        : `${spacing * SPACING_SCALING_FACTOR}px`
    )
    .join(' ');
}

const isUndefined = (spacing?: Spacing) => spacing === undefined;
